// =============================================================================
// TOPLU DÜZENLEME - SÜTUN KATALOĞU
// =============================================================================
// Profil, sonda ve bunlara ait katmanların (horizon) TÜM alanları burada
// tanımlıdır. Amaç "tek tıkla hepsini değiştir" değil; her verinin tek bir
// ekrandan tek tek düzenlenebilmesidir.
//
// Alan tanımı:
//   key         -> full_data içindeki alan adı
//   label       -> başlıkta görünen ad
//   ustSeviye   -> kaydın kendi sütunu (field_profiles.profil_no gibi)
//   secenekler  -> verilince açılır liste olur (serbest yazım kapanır)
//   cokSecim    -> dizi tutan çoklu seçim alanı
//   tip         -> "sayi" | "uzun" | "tarih" | "onay" (yoksa düz metin)
//   genislik    -> sütunun en az genişliği (px)
// =============================================================================

import {
  FIZYOGRAFYA_OPTIONS,
  ANA_MATERYAL_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
  ARAZI_KULLANIM_DETAY_OPTIONS,
  URUN_OPTIONS,
  EGIM_YUZDE_OPTIONS,
  EGIM_YONU_OPTIONS,
  TABII_DRENAJ_OPTIONS,
  SEL_BASMASI_OPTIONS,
  TASKIN_OLASILIGI_OPTIONS,
  EROZYON_SEKLI_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  RUZGAR_ASINDIRMA_OPTIONS,
  RUZGAR_YIGILMA_OPTIONS,
  INSAN_ETKISI_OPTIONS,
  TASLILIK_OPTIONS,
  CAKILLILIK_OPTIONS,
  KAYALILIK_OPTIONS,
  YUZEY_CATLAK_OPTIONS,
  TUZLULUK_OPTIONS,
  ALKALILIK_OPTIONS,
  BTG_OPTIONS,
} from "../../constants/fieldConstants";
import {
  SINIR_BELIRGINLIK_OPTIONS,
  SINIR_SEKIL_OPTIONS,
  TEKSTUR_OPTIONS,
  STRUKTUR_DAYANIKLILIK,
  STRUKTUR_BUYUKLUK,
  STRUKTUR_TIP,
  KIVAM_KURU,
  KIVAM_NEMLI,
  KIVAM_YAS_YAPISKANLIK,
  KIVAM_YAS_PLASTIKLIK,
  KIREC_OPTIONS,
  TASLILIK_HORIZON_OPTIONS,
  KOK_MIKTAR_OPTIONS,
  KOK_KALINLIK_OPTIONS,
  MUNSELL_HUE_OPTIONS,
  MUNSELL_VALUE_CHROMA_OPTIONS,
} from "../../constants/horizonConstants";
import { PROFIL_GRUP_OPTIONS } from "../../constants/identityConstants";

// Secenek listeleri kimi yerde ["a","b"], kimi yerde [{value,label}] geliyor.
// Hucre bilesenine hep duz metin dizisi veriyoruz.
export const duzSecenek = (liste) =>
  (liste || []).map((s) =>
    typeof s === "string" ? s : (s.value ?? s.label ?? String(s)),
  );

// --- PROFİL -----------------------------------------------------------------
export const PROFIL_GRUPLARI = [
  {
    grup: "Kimlik ve Konum",
    alanlar: [
      { key: "profil_no", label: "Profil No", ustSeviye: true, genislik: 100 },
      { key: "cuval_no", label: "Çuval No", genislik: 90 },
      { key: "tanimlayan", label: "Tanımlayan", genislik: 130 },
      { key: "tarih", label: "Tarih", tip: "tarih", genislik: 130 },
      { key: "il", label: "İl", genislik: 110 },
      { key: "ilce", label: "İlçe", genislik: 110 },
      { key: "mahalle", label: "Mahalle", genislik: 130 },
      { key: "toprak_serisi", label: "Toprak Serisi", genislik: 140 },
      { key: "seri_kodu", label: "Seri Kodu", genislik: 90 },
      {
        key: "profilgrup",
        label: "Profil Grubu",
        secenekler: PROFIL_GRUP_OPTIONS,
        genislik: 110,
      },
      { key: "koordinat_x", label: "Koordinat X", genislik: 110 },
      { key: "koordinat_y", label: "Koordinat Y", genislik: 110 },
      { key: "yukseklik", label: "Yükseklik (m)", tip: "sayi", genislik: 100 },
      { key: "iklim", label: "İklim", genislik: 120 },
      {
        key: "ort_sicaklik",
        label: "Ort. Sıcaklık (°C)",
        tip: "sayi",
        genislik: 110,
      },
      { key: "ort_yagis", label: "Ort. Yağış", tip: "sayi", genislik: 100 },
    ],
  },
  {
    grup: "Arazi ve Toprak",
    alanlar: [
      {
        key: "fizyografya",
        label: "Fizyografya",
        secenekler: FIZYOGRAFYA_OPTIONS,
        genislik: 180,
      },
      {
        key: "ana_materyal",
        label: "Ana Materyal",
        secenekler: ANA_MATERYAL_OPTIONS,
        genislik: 170,
      },
      { key: "btg", label: "BTG", secenekler: BTG_OPTIONS, genislik: 200 },
      { key: "kaya_tipi", label: "Kaya Tipi", genislik: 130 },
      { key: "mikrorolyef", label: "Mikrorölyef", genislik: 130 },
      {
        key: "arazi_kullanim_sinif",
        label: "Arazi Kullanım Sınıfı",
        secenekler: ARAZI_KULLANIM_KATEGORI_OPTIONS,
        genislik: 190,
      },
      {
        key: "arazi_kullanim_detay",
        label: "Arazi Kullanım Detayı",
        secenekler: ARAZI_KULLANIM_DETAY_OPTIONS,
        genislik: 190,
      },
      {
        key: "urun",
        label: "Mevcut Ürün",
        secenekler: URUN_OPTIONS,
        genislik: 150,
      },
      {
        key: "arazi_aciklama",
        label: "Arazi Açıklaması",
        tip: "uzun",
        genislik: 220,
      },
      { key: "alinan_ornekler", label: "Alınan Örnekler", genislik: 150 },
    ],
  },
  {
    grup: "Derinlik ve Eğim",
    alanlar: [
      {
        key: "tesirli_derinlik",
        label: "Tesirli Derinlik (cm)",
        tip: "sayi",
        genislik: 130,
      },
      {
        key: "kok_gel_derinlik",
        label: "Kök Gel. Derinliği (cm)",
        tip: "sayi",
        genislik: 140,
      },
      {
        key: "kok_gel_eng_faktor",
        label: "Kök Engel Faktörü",
        genislik: 160,
      },
      {
        key: "egim_yuzde",
        label: "Eğim (%)",
        secenekler: EGIM_YUZDE_OPTIONS,
        genislik: 110,
      },
      {
        key: "egim_yonu",
        label: "Eğim Yönü (Bakı)",
        secenekler: EGIM_YONU_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Su ve Drenaj",
    alanlar: [
      {
        key: "tabii_drenaj",
        label: "Tabii Drenaj",
        secenekler: TABII_DRENAJ_OPTIONS,
        genislik: 160,
      },
      {
        key: "taban_suyu_min",
        label: "Taban Suyu Min (cm)",
        tip: "sayi",
        genislik: 130,
      },
      {
        key: "taban_suyu_max",
        label: "Taban Suyu Max (cm)",
        tip: "sayi",
        genislik: 130,
      },
      {
        key: "taban_suyu_belirti",
        label: "Taban Suyu Belirtisi",
        genislik: 160,
      },
      {
        key: "sel_basmasi",
        label: "Sel Basması",
        secenekler: SEL_BASMASI_OPTIONS,
        genislik: 150,
      },
      {
        key: "taskin_olasiligi",
        label: "Taşkın Olasılığı",
        secenekler: TASKIN_OLASILIGI_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Erozyon ve İnsan Etkisi",
    alanlar: [
      {
        key: "erozyon_sekli",
        label: "Erozyon Şekli",
        secenekler: EROZYON_SEKLI_OPTIONS,
        genislik: 150,
      },
      {
        key: "su_erozyon_derecesi",
        label: "Su Erozyon Derecesi",
        secenekler: SU_EROZYON_DERECESI_OPTIONS,
        genislik: 160,
      },
      {
        key: "ruzgar_asinma",
        label: "Rüzgâr Aşındırma",
        secenekler: RUZGAR_ASINDIRMA_OPTIONS,
        genislik: 150,
      },
      {
        key: "ruzgar_yigilma",
        label: "Rüzgâr Yığılma",
        secenekler: RUZGAR_YIGILMA_OPTIONS,
        genislik: 150,
      },
      {
        key: "insan_etkisi",
        label: "İnsan Etkisi",
        secenekler: INSAN_ETKISI_OPTIONS,
        cokSecim: true,
        genislik: 190,
      },
    ],
  },
  {
    grup: "Yüzey ve Kimya",
    alanlar: [
      {
        key: "taslilik",
        label: "Taşlılık",
        secenekler: TASLILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "cakillilik",
        label: "Çakıllılık",
        secenekler: CAKILLILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "kayalilik",
        label: "Kayalılık",
        secenekler: KAYALILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "yuzey_catlak",
        label: "Yüzey Çatlağı",
        secenekler: YUZEY_CATLAK_OPTIONS,
        genislik: 150,
      },
      {
        key: "tuzluluk",
        label: "Tuzluluk",
        secenekler: TUZLULUK_OPTIONS,
        genislik: 140,
      },
      {
        key: "alkalilik",
        label: "Alkalilik",
        secenekler: ALKALILIK_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Notlar",
    alanlar: [
      {
        key: "dusunceler",
        label: "Notlar / Düşünceler",
        tip: "uzun",
        genislik: 260,
      },
    ],
  },
];

// --- SONDA ------------------------------------------------------------------
export const SONDA_GRUPLARI = [
  {
    grup: "Kimlik",
    alanlar: [
      { key: "sonda_no", label: "Sonda No", ustSeviye: true, genislik: 100 },
      {
        key: "sonda_tipi",
        label: "Sonda Tipi",
        secenekler: ["Örnek", "İstikşafi", "Kontrol"],
        genislik: 120,
      },
      { key: "grup_no", label: "Grup No", genislik: 90 },
      { key: "cuval_no", label: "Çuval No", genislik: 90 },
      { key: "alma_sirasi", label: "Alma Sırası", genislik: 100 },
      { key: "tarih", label: "Tarih", tip: "tarih", genislik: 130 },
      { key: "ornegi_alan", label: "Örneği Alan", genislik: 130 },
      { key: "benzer_profil", label: "Benzer Profil", genislik: 110 },
      { key: "benzer_sonda_no", label: "Benzer Sonda", genislik: 110 },
    ],
  },
  {
    grup: "Konum",
    alanlar: [
      { key: "koordinat_x", label: "Koordinat X", genislik: 110 },
      { key: "koordinat_y", label: "Koordinat Y", genislik: 110 },
      { key: "yukseklik", label: "Yükseklik (m)", tip: "sayi", genislik: 100 },
      { key: "il", label: "İl", genislik: 110 },
      { key: "ilce", label: "İlçe", genislik: 110 },
      { key: "mahalle", label: "Mahalle", genislik: 130 },
      { key: "sehir", label: "Şehir (proje)", genislik: 120 },
    ],
  },
  {
    grup: "Toprak",
    alanlar: [
      {
        key: "ana_materyal",
        label: "Ana Materyal",
        secenekler: ANA_MATERYAL_OPTIONS,
        genislik: 170,
      },
      { key: "btg", label: "BTG", secenekler: BTG_OPTIONS, genislik: 200 },
      {
        key: "fizyografya",
        label: "Fizyografya",
        secenekler: FIZYOGRAFYA_OPTIONS,
        genislik: 180,
      },
    ],
  },
  {
    grup: "Topoğrafya ve Su",
    alanlar: [
      {
        key: "egim",
        label: "Eğim (%)",
        secenekler: EGIM_YUZDE_OPTIONS,
        genislik: 110,
      },
      { key: "derinlik", label: "Derinlik (cm)", tip: "sayi", genislik: 110 },
      {
        key: "drenaj",
        label: "Drenaj",
        secenekler: TABII_DRENAJ_OPTIONS,
        genislik: 160,
      },
      {
        key: "su_erozyonu",
        label: "Su Erozyonu",
        secenekler: SU_EROZYON_DERECESI_OPTIONS,
        genislik: 160,
      },
    ],
  },
  {
    grup: "Yüzey ve Kullanım",
    alanlar: [
      {
        key: "taslilik",
        label: "Taşlılık",
        secenekler: TASLILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "cakillilik",
        label: "Çakıllılık",
        secenekler: CAKILLILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "kayalilik",
        label: "Kayalılık",
        secenekler: KAYALILIK_OPTIONS,
        genislik: 160,
      },
      {
        key: "urun",
        label: "Ürün",
        secenekler: URUN_OPTIONS,
        genislik: 150,
      },
      {
        key: "arazi_kullanim_sinif",
        label: "Arazi Kullanım Sınıfı",
        secenekler: ARAZI_KULLANIM_KATEGORI_OPTIONS,
        genislik: 190,
      },
    ],
  },
  {
    grup: "Notlar",
    alanlar: [
      { key: "gozlemler", label: "Gözlemler", tip: "uzun", genislik: 260 },
    ],
  },
];

// --- PROFİL KATMANI (horizon) ------------------------------------------------
export const PROFIL_KATMAN_GRUPLARI = [
  {
    grup: "Katman Kimliği",
    alanlar: [
      { key: "no", label: "Sıra", tip: "sayi", genislik: 70 },
      { key: "sembol", label: "Sembol", genislik: 90 },
      {
        key: "derinlik_min",
        label: "Derinlik Min",
        tip: "sayi",
        genislik: 100,
      },
      {
        key: "derinlik_max",
        label: "Derinlik Max",
        tip: "sayi",
        genislik: 100,
      },
      { key: "derinlik_arti", label: "Derinlik +", tip: "onay", genislik: 90 },
      {
        key: "ornek_alinamadi",
        label: "Örnek Alınamadı",
        tip: "onay",
        genislik: 110,
      },
    ],
  },
  {
    grup: "Bünye ve Renk",
    alanlar: [
      {
        key: "tekstur",
        label: "Tekstür",
        secenekler: TEKSTUR_OPTIONS,
        genislik: 180,
      },
      {
        key: "renk_sinif_kuru",
        label: "Kuru Hue",
        secenekler: MUNSELL_HUE_OPTIONS,
        genislik: 110,
      },
      {
        key: "renk_kod_kuru",
        label: "Kuru Value/Chroma",
        secenekler: MUNSELL_VALUE_CHROMA_OPTIONS,
        genislik: 130,
      },
      {
        key: "renk_sinif_yas",
        label: "Nemli Hue",
        secenekler: MUNSELL_HUE_OPTIONS,
        genislik: 110,
      },
      {
        key: "renk_kod_yas",
        label: "Nemli Value/Chroma",
        secenekler: MUNSELL_VALUE_CHROMA_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Strüktür ve Kıvam",
    alanlar: [
      {
        key: "struktur_dayaniklilik",
        label: "Strüktür Dayanıklılık",
        secenekler: STRUKTUR_DAYANIKLILIK,
        genislik: 160,
      },
      {
        key: "struktur_buyukluk",
        label: "Strüktür Büyüklük",
        secenekler: STRUKTUR_BUYUKLUK,
        genislik: 150,
      },
      {
        key: "struktur_tip",
        label: "Strüktür Tipi",
        secenekler: STRUKTUR_TIP,
        cokSecim: true,
        genislik: 180,
      },
      {
        key: "kivam_kuru",
        label: "Kıvam (Kuru)",
        secenekler: KIVAM_KURU,
        genislik: 140,
      },
      {
        key: "kivam_nemli",
        label: "Kıvam (Nemli)",
        secenekler: KIVAM_NEMLI,
        genislik: 140,
      },
      {
        key: "kivam_yas_yapiskanlik",
        label: "Yaş Yapışkanlık",
        secenekler: KIVAM_YAS_YAPISKANLIK,
        genislik: 150,
      },
      {
        key: "kivam_yas_plastiklik",
        label: "Yaş Plastiklik",
        secenekler: KIVAM_YAS_PLASTIKLIK,
        genislik: 150,
      },
    ],
  },
  {
    grup: "Kökler",
    alanlar: [
      {
        key: "kok_sacak_miktar",
        label: "Saçak Kök Miktarı",
        secenekler: KOK_MIKTAR_OPTIONS,
        genislik: 150,
      },
      {
        key: "kok_sacak_kalinlik",
        label: "Saçak Kök Kalınlığı",
        secenekler: KOK_KALINLIK_OPTIONS,
        genislik: 150,
      },
      {
        key: "kok_kazik_miktar",
        label: "Kazık Kök Miktarı",
        secenekler: KOK_MIKTAR_OPTIONS,
        genislik: 150,
      },
      {
        key: "kok_kazik_kalinlik",
        label: "Kazık Kök Kalınlığı",
        secenekler: KOK_KALINLIK_OPTIONS,
        genislik: 150,
      },
    ],
  },
  {
    grup: "Diğer Özellikler",
    alanlar: [
      {
        key: "kirec",
        label: "Kireç",
        secenekler: KIREC_OPTIONS,
        genislik: 130,
      },
      {
        key: "taslilik",
        label: "Taşlılık",
        secenekler: TASLILIK_HORIZON_OPTIONS,
        genislik: 160,
      },
      {
        key: "sinir_belirginlik",
        label: "Sınır Belirginliği",
        secenekler: SINIR_BELIRGINLIK_OPTIONS,
        genislik: 150,
      },
      {
        key: "sinir_sekil",
        label: "Sınır Şekli",
        secenekler: SINIR_SEKIL_OPTIONS,
        genislik: 130,
      },
      {
        key: "ozel_gorunum",
        label: "Özel Görünüm",
        tip: "uzun",
        genislik: 220,
      },
    ],
  },
];

// --- SONDA KATMANI ----------------------------------------------------------
export const SONDA_KATMAN_GRUPLARI = [
  {
    grup: "Katman",
    alanlar: [
      { key: "no", label: "Sıra", tip: "sayi", genislik: 70 },
      { key: "sembol", label: "Sembol", genislik: 90 },
      {
        key: "derinlik_min",
        label: "Derinlik Min",
        tip: "sayi",
        genislik: 100,
      },
      {
        key: "derinlik_max",
        label: "Derinlik Max",
        tip: "sayi",
        genislik: 100,
      },
      {
        key: "tekstur",
        label: "Tekstür",
        secenekler: TEKSTUR_OPTIONS,
        genislik: 180,
      },
      {
        key: "kirec",
        label: "Kireç",
        secenekler: KIREC_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Renk",
    alanlar: [
      {
        key: "renk_sinif_kuru",
        label: "Kuru Hue",
        secenekler: MUNSELL_HUE_OPTIONS,
        genislik: 110,
      },
      {
        key: "renk_kod_kuru",
        label: "Kuru Value/Chroma",
        secenekler: MUNSELL_VALUE_CHROMA_OPTIONS,
        genislik: 130,
      },
      {
        key: "renk_sinif_yas",
        label: "Nemli Hue",
        secenekler: MUNSELL_HUE_OPTIONS,
        genislik: 110,
      },
      {
        key: "renk_kod_yas",
        label: "Nemli Value/Chroma",
        secenekler: MUNSELL_VALUE_CHROMA_OPTIONS,
        genislik: 130,
      },
    ],
  },
  {
    grup: "Not",
    alanlar: [
      { key: "gozlem", label: "Gözlem", tip: "uzun", genislik: 240 },
    ],
  },
];

// Sekme tanimlari: her sekme kendi sutun katalogunu ve varsayilan sutunlarini
// tasir. katman=true olanlarda satirlar horizon dizisinden acilir.
export const SEKMELER = {
  profil: {
    etiket: "Profiller",
    gruplar: PROFIL_GRUPLARI,
    katman: false,
    varsayilan: [
      "profil_no",
      "toprak_serisi",
      "seri_kodu",
      "profilgrup",
      "btg",
      "tesirli_derinlik",
      "egim_yuzde",
      "arazi_kullanim_sinif",
    ],
  },
  profilKatman: {
    etiket: "Profil Katmanları",
    gruplar: PROFIL_KATMAN_GRUPLARI,
    katman: true,
    varsayilan: [
      "no",
      "sembol",
      "derinlik_min",
      "derinlik_max",
      "tekstur",
      "renk_sinif_kuru",
      "renk_kod_kuru",
      "kirec",
    ],
  },
  sonda: {
    etiket: "Sondalar",
    gruplar: SONDA_GRUPLARI,
    katman: false,
    varsayilan: [
      "sonda_no",
      "sonda_tipi",
      "benzer_profil",
      "derinlik",
      "egim",
      "btg",
      "koordinat_x",
      "koordinat_y",
    ],
  },
  sondaKatman: {
    etiket: "Sonda Katmanları",
    gruplar: SONDA_KATMAN_GRUPLARI,
    katman: true,
    varsayilan: [
      "no",
      "sembol",
      "derinlik_min",
      "derinlik_max",
      "tekstur",
      "kirec",
    ],
  },
};

// Bir sekmenin butun alanlarini duz liste olarak verir.
export const gruplariDuzles = (gruplar) =>
  gruplar.reduce((hepsi, g) => hepsi.concat(g.alanlar), []);
